import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CrmInviteData,
  ExternalMemberInviteData,
  NetworkInviteLinkModel,
  NetworkInviteModel,
} from '../../types/invites';

interface CommunityOverviewProps {
  invite: NetworkInviteModel | NetworkInviteLinkModel | ExternalMemberInviteData | CrmInviteData;
}

export function CommunityOverview({ invite }: CommunityOverviewProps): JSX.Element {
  const { t } = useTranslation();

  // TODO: consider adding the missing fields to the api responses
  const moderators: { profilePicture: string; alias: string }[] | undefined = [];
  const memberProfilePictures = undefined;
  const memberCount = undefined;

  return (
    <div className="flex w-full flex-col items-center p-8">
      {invite.picture ? (
        <img
          src={invite.picture}
          alt="Community Overview"
          className="aspect-square w-2/5 rounded-lg object-contain p-4"
        />
      ) : (
        <img
          src={`${process.env.PUBLIC_URL}/images/figures/phone.svg`}
          alt="Community Overview"
          className="aspect-square w-2/5 rounded-lg bg-primary-300 object-contain p-4"
        />
      )}
      <h1 className="mt-4 text-xl font-semibold text-primary-900">
        {t('page.businessInvite.title', { communityName: invite.name })}
      </h1>
      {moderators?.length > 0 && memberProfilePictures && (
        <div className="mt-6 flex w-full flex-col justify-between gap-4 md:flex-row">
          <div className="flex items-center gap-4">
            <img
              src={
                moderators?.[0]?.profilePicture
                  ? moderators?.[0]?.profilePicture
                  : `${process.env.PUBLIC_URL}/images/figures/phone.svg`
              }
              alt="Moderator"
              className="h-8 w-8 rounded-full bg-primary-300"
            />
            <div>
              <h3 className="text-sm font-semibold text-primary-900 text-opacity-50">
                {t('page.businessInvite.moderatedBy')}
              </h3>
              <h3>
                {moderators
                  ?.map((moderator) => moderator.alias)
                  ?.slice(0, 2)
                  ?.join(', ') || ''}
              </h3>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-row items-center justify-center">
              <img
                src={
                  memberProfilePictures?.[0]
                    ? memberProfilePictures[0]
                    : `${process.env.PUBLIC_URL}/images/figures/phone.svg`
                }
                alt="Moderator"
                className="relative z-30 h-10 w-10 rounded-full border-[3px] border-[#fdf6f4] bg-primary-300"
              />
              {memberCount && memberCount > 1 && (
                <img
                  src={
                    memberProfilePictures?.[1]
                      ? memberProfilePictures[1]
                      : `${process.env.PUBLIC_URL}/images/figures/phone.svg`
                  }
                  alt="Moderator"
                  className="relative z-20 -ml-4 h-10 w-10 rounded-full border-[3px] border-[#fdf6f4] bg-primary-300"
                />
              )}
              {memberCount && memberCount > 2 && (
                <img
                  src={
                    memberProfilePictures?.[2]
                      ? memberProfilePictures[2]
                      : `${process.env.PUBLIC_URL}/images/figures/phone.svg`
                  }
                  alt="Moderator"
                  className="relative z-10 -ml-4 h-10 w-10 rounded-full border-[3px] border-[#fdf6f4] bg-primary-300"
                />
              )}
            </div>
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-primary-900 text-opacity-50">Leden</h3>
              <h3 className="font-medium">
                {memberCount === 1
                  ? t('page.businessInvite.members.one', { count: memberCount })
                  : t('page.businessInvite.members.other', { count: memberCount })}
              </h3>
            </div>
          </div>
        </div>
      )}
      <div className="mt-4 w-full text-left">
        {invite?.description && (
          <>
            <h2 className="text-lg font-semibold text-primary-900">About {invite.name}</h2>
            <p className="text-md max-h-32 overflow-y-auto text-primary-900 text-opacity-50">{invite?.description}</p>
          </>
        )}
      </div>
    </div>
  );
}
